import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { API_BASE } from '../config';
import { Alert, Button } from './ComponentLibrary';

const INITIAL_VISIBLE_COUNT = 5;
const REVEAL_INCREMENT = 3;
// A highlight reel, not an exhaustive POI dump - applies to every listing
// (not just short-let), even though the server-side pool can return up to
// ~27 candidates across 9 categories.
const MAX_TOTAL_PLACES = 8;

const CATEGORY_META = [
  { key: 'hospital', label: 'Hospital/clinic', icon: '🏥' },
  { key: 'supermarket', label: 'Supermarket', icon: '🛒' },
  { key: 'school', label: 'School', icon: '🏫' },
  { key: 'transit_station', label: 'Transport', icon: '🚉' },
  { key: 'bank', label: 'Bank/ATM', icon: '🏦' },
  { key: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { key: 'airport', label: 'Airport', icon: '✈️' },
  { key: 'tourist_attraction', label: 'Landmark', icon: '🏛️' },
  { key: 'shopping_mall', label: 'Shopping & leisure', icon: '🛍️' },
];
const CATEGORY_META_BY_KEY = Object.fromEntries(CATEGORY_META.map((c) => [c.key, c]));

// A short-let guest is asking "why would I want to stay here" - an
// airport, a landmark, somewhere to eat - not "where's the nearest bank",
// which matters far more to someone actually living there long-term. Added
// directly onto a place's score (see scorePlace) rather than replacing
// distance/popularity outright, so a genuinely far-away airport still
// loses to a great, close restaurant - "geographically relevant" in the
// sense that the boost only wins when the place is also plausibly nearby,
// not a blanket "always show an airport" rule.
const SHORT_LET_CATEGORY_WEIGHT = {
  airport: 0.35,
  tourist_attraction: 0.3,
  restaurant: 0.2,
  shopping_mall: 0.2,
  transit_station: 0.15,
};

// A resident's "is this a good area to live" question weighs healthcare/
// schooling/errands over dining and leisure - the opposite emphasis from
// SHORT_LET_CATEGORY_WEIGHT above. Without some weight here, ranking falls
// back to pure distance/popularity, which in a dense mixed-use area (lots
// of restaurants clustered near any residential block) can crowd out
// hospitals/schools entirely even though they're just as close - exactly
// the "useful mix, not five restaurants" this ranking exists to prevent.
const RESIDENT_CATEGORY_WEIGHT = {
  hospital: 0.3,
  school: 0.25,
  supermarket: 0.2,
  transit_station: 0.15,
  bank: 0.1,
};

// Closer scores higher but flattens out past a few km (a 0.2km vs 0.5km
// hospital shouldn't dominate a 6km vs a 15km one). log10(reviews) turns
// Google's user_ratings_total (which spans single digits to tens of
// thousands) into a comparable 0-1ish popularity signal instead of letting
// a place with 3000 reviews swamp everything else outright. Neither field
// is guaranteed by Google, so both default to a neutral middle value
// rather than zeroing out (and effectively disqualifying) a real,
// close-by place that just has no rating data yet.
function scorePlace(place, categoryWeight = 0) {
  const distanceScore = 1 / (1 + Math.max(place.distanceKm, 0));
  const popularityScore = place.userRatingsTotal
    ? Math.min(Math.log10(place.userRatingsTotal + 1) / 4, 1)
    : 0.3;
  const ratingScore = place.rating ? place.rating / 5 : 0.5;
  return distanceScore * 0.55 + popularityScore * 0.25 + ratingScore * 0.2 + categoryWeight;
}

// At most this many of the same category in a row before a different
// category has to earn its way back in - the mechanism that stops "5
// supermarkets" while still allowing a genuinely strong pair (the user's
// own short-let example includes two landmarks) rather than forcing
// exactly-one-of-each like a strict round-robin would.
const MAX_CONSECUTIVE_PER_CATEGORY = 2;

/**
 * Flattens the per-category candidate pool from the API into one ordered
 * list, ranked overall by relevance (distance + popularity + rating, plus
 * a traveller-relevance boost for short-let listings - see
 * SHORT_LET_CATEGORY_WEIGHT), while still capping how many of the same
 * category can cluster at the front: without a cap, a dense cluster of
 * five nearby supermarkets would out-score a single nearby hospital on
 * pure relevance and crowd out every other category. Exported for direct
 * testing of the ranking itself, independent of the fetch/rendering
 * around it.
 */
export function buildDiversifiedPlaceList(categories, { isShortStay } = {}) {
  const weights = isShortStay ? SHORT_LET_CATEGORY_WEIGHT : RESIDENT_CATEGORY_WEIGHT;

  const allCandidates = [];
  for (const [key, places] of Object.entries(categories)) {
    for (const place of places || []) {
      allCandidates.push({ ...place, category: key, score: scorePlace(place, weights[key] || 0) });
    }
  }
  allCandidates.sort((a, b) => b.score - a.score);

  const result = [];
  const deferred = [];
  const categoryCounts = {};
  for (const candidate of allCandidates) {
    const count = categoryCounts[candidate.category] || 0;
    if (count < MAX_CONSECUTIVE_PER_CATEGORY) {
      result.push(candidate);
      categoryCounts[candidate.category] = count + 1;
    } else {
      deferred.push(candidate);
    }
  }
  // Anything held back by the per-category cap is still relevance-ordered,
  // just appended after the diverse front of the list instead of dropped.
  return [...result, ...deferred];
}

/**
 * "Getting around" - real nearby places looked up live from the property's
 * own saved coordinates (see GET /properties/:id/nearby-places ->
 * getNearbyPlaces in server/geocode.js). Replaces the old hand-curated
 * per-state landmark list (client/src/nearbyLandmarks.js, since removed) -
 * nothing here is hard-coded, so it works the same for every property in
 * every state rather than only the handful that had curated entries.
 *
 * Shows the 5 most relevant places up front (see buildDiversifiedPlaceList)
 * with a "Show more" control that reveals 3 at a time, capped at 8 total -
 * a highlight reel, not a dump of the full ~27-place server-side pool.
 */
function PropertyNearbyPlaces({ propertyId, isShortStay }) {
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error
  const [categories, setCategories] = useState({});
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  // Defer the network call (up to 9 billed Google Places requests per
  // property, see server/geocode.js) until this section actually scrolls
  // into view. It previously fired unconditionally on mount, which meant
  // every property page load - including the SEO prerender job's headless
  // visits, which never scroll - paid for it even when no one ever saw this
  // part of the page.
  useEffect(() => {
    if (!propertyId || isInView) return;
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsInView(true); // fail open rather than never load the section
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [propertyId, isInView]);

  useEffect(() => {
    if (!propertyId) {
      setStatus('empty');
      return;
    }
    if (!isInView) return;

    let cancelled = false;
    setStatus('loading');
    setVisibleCount(INITIAL_VISIBLE_COUNT);

    fetch(`${API_BASE}/properties/${encodeURIComponent(propertyId)}/nearby-places`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const resultCategories = data.categories || {};
        const hasAnyResult = Object.values(resultCategories).some((places) => places?.length > 0);
        setCategories(resultCategories);
        setStatus(hasAnyResult ? 'ready' : 'empty');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Nearby places fetch failed:', err.message);
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [propertyId, isInView]);

  const rankedPlaces = useMemo(
    () => buildDiversifiedPlaceList(categories, { isShortStay }),
    [categories, isShortStay],
  );
  const visiblePlaces = rankedPlaces.slice(0, Math.min(visibleCount, MAX_TOTAL_PLACES));
  const remainingCount = Math.min(rankedPlaces.length, MAX_TOTAL_PLACES) - visiblePlaces.length;

  // Nothing to show and nothing to explain (e.g. property has no
  // coordinates at all) - matches the old component's philosophy of
  // just not rendering the section rather than showing an empty shell.
  if (status === 'empty') {
    return null;
  }

  return (
    <div ref={containerRef} className="card property-detail-sections-flat__section">
      <h2 className="property-detail-sections-flat__heading">Getting around</h2>

      {status === 'loading' && (
        <p className="nearby-places__status">Looking up nearby places…</p>
      )}

      {status === 'error' && (
        <Alert type="error">Nearby places aren&apos;t available right now. Try again later.</Alert>
      )}

      {status === 'ready' && (
        <>
          <ul className="nearby-landmarks-list">
            {visiblePlaces.map((place) => {
              const meta = CATEGORY_META_BY_KEY[place.category];
              return (
                <li key={place.placeId || place.name} className="nearby-landmarks-list__item">
                  <a
                    className="nearby-landmarks-list__link"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}&destination_place_id=${
                      place.placeId || ''
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Get directions to ${place.name} (${meta?.label}), ${place.distanceKm.toFixed(1)} km away`}
                  >
                    <span>
                      <span aria-hidden="true">{meta?.icon}</span> {place.name}
                    </span>
                    <strong>{place.distanceKm.toFixed(1)} km</strong>
                  </a>
                </li>
              );
            })}
          </ul>

          {(remainingCount > 0 || visibleCount > INITIAL_VISIBLE_COUNT) && (
            <div className="nearby-places__reveal-controls">
              {remainingCount > 0 && (
                <Button
                  variant="secondary"
                  className="nearby-places__show-more"
                  onClick={() => setVisibleCount((count) => count + REVEAL_INCREMENT)}
                >
                  Show {Math.min(remainingCount, REVEAL_INCREMENT)} more
                </Button>
              )}
              {visibleCount > INITIAL_VISIBLE_COUNT && (
                <Button
                  variant="secondary"
                  className="nearby-places__show-less"
                  onClick={() => setVisibleCount(INITIAL_VISIBLE_COUNT)}
                >
                  Show less
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

PropertyNearbyPlaces.propTypes = {
  propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isShortStay: PropTypes.bool,
};

export default PropertyNearbyPlaces;
